import React, { useState } from "react";
import {
  MessageSquarePlus,
  Send,
  Bug,
  Lightbulb,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { track } from "@vercel/analytics";
import { useSidebar } from "@/components/ui/sidebar";

type FeedbackType = "bug" | "suggestion";

const FORMSPREE_ENDPOINT = "https://formspree.io/f/xpqzvolr";

export const FeedbackButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<FeedbackType>("suggestion");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { state } = useSidebar();
  const isExpanded = state === "expanded";

  const handleSubmit = async () => {
    if (!message.trim()) return;

    setIsSubmitting(true);
    try {
      // Send to Formspree
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type: feedbackType,
          email: email.trim() || undefined,
          message: message.trim(),
          timestamp: new Date().toISOString(),
          url: window.location.href,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to submit feedback");
      }

      // Track the feedback in analytics
      track("feedback_submitted", {
        type: feedbackType,
        message_length: message.length,
      });

      toast({
        title: "Thanks for your feedback!",
        description: "We appreciate you taking the time to help us improve.",
      });

      setEmail("");
      setMessage("");
      setIsOpen(false);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast({
        title: "Error",
        description: "Failed to submit feedback. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="px-3 py-3 border-t border-sidebar-border mt-auto">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size={isExpanded ? "default" : "icon"}
            className={`w-full flex items-center ${
              isExpanded ? "justify-start" : "justify-center"
            } text-muted-foreground hover:text-foreground hover:bg-sidebar-accent transition-all duration-200`}
          >
            <MessageSquarePlus
              className={`h-4 w-4 ${isExpanded ? "mr-2" : ""}`}
            />
            {isExpanded && <span>Send Feedback</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          side="right"
          align="end"
          className="w-[340px] p-0 overflow-hidden border-0 shadow-2xl rounded-xl"
        >
          {/* Header with gradient */}
          <div className="relative px-5 pt-5 pb-4 bg-gradient-to-br from-ivy-green/10 via-ivy-emerald/5 to-transparent dark:from-ivy-green/20 dark:via-ivy-emerald/10">
            <h4 className="font-semibold text-base text-foreground">
              Lay it on us!
            </h4>
            <p className="text-sm text-muted-foreground mt-0.5">
              Help us make Ivy better for you
            </p>
          </div>

          <div className="p-5 pt-4 space-y-4 bg-background">
            {/* Feedback type selector - pill style */}
            <div className="flex gap-2 p-1 bg-muted/50 rounded-lg">
              <button
                onClick={() => setFeedbackType("bug")}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-md text-sm font-medium transition-all duration-200 ${
                  feedbackType === "bug"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Bug
                  className={`h-4 w-4 ${
                    feedbackType === "bug"
                      ? "text-red-500"
                      : "text-muted-foreground"
                  }`}
                />
                <span>Bug Report</span>
              </button>
              <button
                onClick={() => setFeedbackType("suggestion")}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-md text-sm font-medium transition-all duration-200 ${
                  feedbackType === "suggestion"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Lightbulb
                  className={`h-4 w-4 ${
                    feedbackType === "suggestion"
                      ? "text-amber-500"
                      : "text-muted-foreground"
                  }`}
                />
                <span>Idea</span>
              </button>
            </div>

            {/* Email field */}
            <div>
              <Input
                type="email"
                placeholder="Your email (optional)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border-muted-foreground/20 focus:border-ivy-green/50 transition-colors duration-200 bg-muted/30 focus:bg-background"
              />
            </div>

            {/* Textarea with animated border */}
            <div className="relative group">
              <Textarea
                placeholder={
                  feedbackType === "bug"
                    ? "What went wrong? Tell us about the issue..."
                    : "What would make Ivy better for you?"
                }
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className="resize-none border-muted-foreground/20 focus:border-ivy-green/50 transition-colors duration-200 bg-muted/30 focus:bg-background"
              />
              <div className="absolute bottom-2 right-2 text-xs text-muted-foreground/50">
                {message.length > 0 && `${message.length} chars`}
              </div>
            </div>

            {/* Submit button with gradient */}
            <Button
              className="w-full h-11 bg-gradient-to-r from-ivy-green to-ivy-emerald hover:from-ivy-green/90 hover:to-ivy-emerald/90 text-white font-medium shadow-lg shadow-ivy-green/20 transition-all duration-200 hover:shadow-xl hover:shadow-ivy-green/30 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-lg"
              onClick={handleSubmit}
              disabled={!message.trim() || isSubmitting}
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Sending...</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Send className="h-4 w-4" />
                  <span>Send Feedback</span>
                </div>
              )}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default FeedbackButton;
